import { usePollAISuggestionRequest } from 'hooks/queries/articles/aiSuggestionsRequests/usePollAISuggestionRequest'
import { GenerateRequest, SuggestionRequestStatus } from 'types'
import { showToast } from 'common/ToastMessage'
import { FC, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { MessageType } from 'antd/lib/message'
import { sendToApi } from 'api/client'
import { useAuth } from 'hooks/UseAuth'
import { useShowFailedGenerateRequestToast } from 'screens/ProseEdit/components/ProcessingSuggestionToast/useShowFailedGenerateRequestToast'
import { ProcessingToastMessage } from 'screens/ProseEdit/components/ProcessingSuggestionToast/ProcessingToastMessage'
import { EditorView } from 'prosemirror-view'

interface Props {
  id: number
  articleId: string
  layerId: string
  onStreaming?: (jobId: string) => any
  onSuccess?: () => any
  onAbort?: () => any
  view?: EditorView
}

export const PollSuggestionRequest: FC<React.PropsWithChildren<Props>> = observer(
  ({ id, articleId, layerId, onStreaming, onSuccess, onAbort, view }) => {
    const toastRef = useRef<MessageType | null>(null)
    const auth = useAuth()
    const showFailedGenerateRequest = useShowFailedGenerateRequestToast({ view })

    useEffect(() => {
      return () => {
        toastRef.current?.()
        toastRef.current = null
      }
    }, [id])

    const handleCancel = async () => {
      await sendToApi<{}>(`/articles/${articleId}/layers/${layerId}/ai_suggestion_requests/${id}/abort`, {
        method: 'POST',
      })
    }

    usePollAISuggestionRequest(
      {
        articleId,
        layerId,
        id,
      },
      {
        onSuccess: async ({ request }) => {
          const isProcessing = [SuggestionRequestStatus.Pending, SuggestionRequestStatus.Processing].includes(
            request.status,
          )
          const isComplete = [
            SuggestionRequestStatus.Successful,
            SuggestionRequestStatus.Failed,
            SuggestionRequestStatus.Aborted,
          ].includes(request.status)

          if (!isComplete && request.job_id) {
            onStreaming?.(request.job_id)
          }

          const isFailed = request.status === SuggestionRequestStatus.Failed
          const isAborted = request.status === SuggestionRequestStatus.Aborted
          const isSuccessful = request.status === SuggestionRequestStatus.Successful

          if (isProcessing && !toastRef.current) {
            toastRef.current = showToast(
              <ProcessingToastMessage
                text="Generating your content..."
                onCancel={handleCancel}
                showCancelButton={!!auth && auth.id === request.requester?.id}
              />,
              0,
            )
          }

          if (isComplete) {
            toastRef.current?.()
            toastRef.current = null

            if (isSuccessful) {
              onSuccess?.()
            }

            if (isAborted) {
              onAbort?.()
            }
          }

          if (isFailed) {
            showFailedGenerateRequest(request as unknown as GenerateRequest)
          }
        },
      },
    )

    return null
  },
)
